import { format } from 'date-fns';
import { MonitorAlertFullDto } from '@/app/Alert/types';
import { Time } from '@/components/ui/time';
import { StatusBadge } from '@/components/ui/status-badge';
import { getStatusConfig } from '@/app/AlertsDashboard/utils';
import { Search, Loader2 } from 'lucide-react';
import { useEffect, useState, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';

interface AlertSearchResultsProps {
  results: MonitorAlertFullDto[];
  isLoading: boolean;
  searchText: string;
  siteName: string;
  customerName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  searchType: string;
  onClose: () => void;
}

export function AlertSearchResults({
  results,
  isLoading,
  searchText,
  siteName,
  customerName,
  startDate,
  endDate,
  searchType,
  onClose,
}: AlertSearchResultsProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const isWrappingAround = useRef(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!results.length) return;

      let nextIndex: number;
      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          nextIndex = (selectedIndex + 1) % results.length;
          isWrappingAround.current = nextIndex === 0;
          setSelectedIndex(nextIndex);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          nextIndex = (selectedIndex - 1 + results.length) % results.length;
          isWrappingAround.current = nextIndex === results.length - 1;
          setSelectedIndex(nextIndex);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (results[selectedIndex]) {
            window.location.href = `/alert/${results[selectedIndex].id}`;
            onClose();
          }
          break;
        }
      }
    },
    [results, selectedIndex, onClose]
  );

  // Scroll selected item into view when selection changes
  useEffect(() => {
    if (selectedItemRef.current && scrollContainerRef.current) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: isWrappingAround.current ? 'auto' : 'smooth',
      });
      isWrappingAround.current = false;
    }
  }, [selectedIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="mb-2 rounded-full bg-card2 p-3">
          <Loader2 className="h-5 w-5 animate-spin text-lime" />
        </div>
        <p className="text-sm text-muted-foreground">Loading results...</p>
      </div>
    );
  }

  // Show empty state for site or customer search when no search text is entered
  if (searchType !== 'alert' && !searchText) {
    return (
      <div className="flex flex-col items-center justify-center py-4">
        <div className="mb-2 rounded-full bg-card2 p-3">
          <Search className="h-5 w-5 text-lime" />
        </div>
        <p className="text-sm text-muted-foreground">
          {searchType === 'site'
            ? 'Start searching for site names'
            : 'Start searching for customers'}
        </p>
      </div>
    );
  }

  // Show results when any search field has text or date range is selected
  const hasSearchCriteria = searchText || siteName || customerName || startDate || endDate;
  if (!hasSearchCriteria) {
    return;
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-4">
        <div className="mb-2 rounded-full bg-card2 p-3">
          <Search className="h-5 w-5 text-lime" />
        </div>
        <p className="text-sm text-muted-foreground">
          No results found for{' '}
          <span className="font-semibold">
            {[
              searchText && `"${searchText}"`,
              siteName && `site "${siteName}"`,
              customerName && `customer "${customerName}"`,
              startDate && `from ${format(startDate, 'PPp')}`,
              endDate && `to ${format(endDate, 'PPp')}`,
            ]
              .filter(Boolean)
              .join(', ')}
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div ref={scrollContainerRef} className="max-h-[400px] overflow-y-auto">
        <div className="space-y-1 p-2">
          {results.map((alert, index) => (
            <div
              key={alert.id}
              className="relative"
              ref={index === selectedIndex ? selectedItemRef : undefined}
            >
              <a
                href={`/alert/${alert.id}`}
                onClick={onClose}
                className={cn(
                  'flex items-start gap-3 overflow-visible rounded-md p-2 px-0 hover:bg-[#1b1b1b]',
                  results.indexOf(alert) === selectedIndex && 'bg-[#1b1b1b]'
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{alert.alertId}</span>
                    <span className="text-xs text-muted-foreground">
                      <Time date={alert.occurred} noTooltip />
                    </span>
                  </div>
                  <p className="truncate text-sm text-muted-foreground">
                    {alert.companyName} - {alert.projectName}
                  </p>
                </div>
                {alert.status && (
                  <StatusBadge
                    status={getStatusConfig(alert.status).name}
                    color={getStatusConfig(alert.status).color}
                  />
                )}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
